import { useQuery } from '@apollo/client';
import { isEmpty, isNil } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Queries } from '@shared/api';
import { Field } from '@shared/data/entity/fields/Field';
import { CardEntityError } from '@shared/scenes/entity/create/hooks/useFormErrors';
import { API } from '@shared/types/api';
import { useProfile } from '@shared/util/hooks';
import { FieldRecord, GetUpdatedField, UpdateField } from '../../types';
import { buildFieldRecord } from '../../util/buildFieldRecord';
import { getModifiedFields, isFieldModified } from '../../util/isFieldRecordModified';

export interface EditFieldsHook {
  fields: FieldRecord;
  rawFields?: API.WorkflowEntity['fields'] | null;
  originalFields: FieldRecord | null;
  modifiedFields: FieldRecord | null;
  threadId?: string;
  errors: CardEntityError[];
  loading: boolean;
  isDirty: boolean;
  updateField: UpdateField;
  resetDirty: () => void;
}

export function useEditFields(
  entityId: string,
  workflowTemplate?: API.WorkflowTemplateFragment,
): EditFieldsHook {
  const { data, loading, error } = useQuery<
    API.GetWorkflowEntityQuery,
    API.GetWorkflowEntityQueryVariables
  >(Queries.workflow.getWorkflowEntity, {
    variables: { input: { id: entityId } },
    skip: isNil(entityId),
  });
  const { profile } = useProfile();
  const [originalFields, setOriginalFields] = useState<FieldRecord | null>(null);
  const [fields, setFields] = useState<FieldRecord>({});
  const modifiedFields = useMemo(
    () => getModifiedFields(originalFields, fields),
    [originalFields, fields],
  );

  useEffect(() => {
    if (entityId) {
      setFields({});
      setOriginalFields(null);
    }
  }, [entityId]);

  const updateField = useCallback(
    (componentId: string, field: Field | null | GetUpdatedField, skipEqualityCheck = false) => {
      setFields(previousFields => {
        const previousField = previousFields[componentId];

        let updatedField: Field | null;
        if (typeof field === 'function') {
          updatedField = field(previousField);
        } else {
          updatedField = field;
        }

        if (skipEqualityCheck || isFieldModified(previousField, updatedField)) {
          return { ...previousFields, [componentId]: updatedField };
        } else {
          return previousFields;
        }
      });
    },
    [],
  );

  const isDirty = !isNil(modifiedFields) && Object.keys(modifiedFields).length > 0;
  const resetDirty = useCallback(() => setOriginalFields(fields), [fields]);

  useEffect(() => {
    const entity = data?.workflowEntity?.entity;
    const refs = data?.workflowEntity?.refs;
    const components = workflowTemplate?.components;
    const fieldsHaveBeenSet = !isEmpty(fields) || !isEmpty(originalFields);
    if (entity && refs && components && !fieldsHaveBeenSet) {
      const fieldRecord = buildFieldRecord({
        entity,
        components,
        refs,
        currentUser: profile,
      });
      setOriginalFields(fieldRecord);
      setFields(fieldRecord);
    }
  }, [data, fields, originalFields, workflowTemplate?.components, profile]);

  return {
    fields,
    rawFields: data?.workflowEntity?.entity.fields,
    originalFields,
    modifiedFields,
    threadId: data?.workflowEntity?.entity.threadId,
    updateField,
    loading,
    isDirty,
    resetDirty,
    errors: !isNil(error) ? [{ description: error.message }] : [],
  };
}
